import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Collapse,
  alpha,
  useTheme,
  Grid,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  FilterList,
  Visibility,
  MoreVert,
  Search,
  Close,
  CheckCircle,
  Cancel,
  HelpOutline,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  fetchAssessmentAttempts,
  fetchAnalyticsDashboard,
  fetchAttemptQuestionAnalytics,
} from '@/store';
import type { RootState, AppDispatch } from '@/store/types';
import type { AssessmentAttemptsQuery } from '@/services/analytics/analyticsService';
import { useNavigate } from 'react-router-dom';

interface AdvancedAnalyticsProps {
  categories: Array<{ _id: string; name: string; isActive: boolean }>;
  assessments: Array<{ _id: string; title: string; category: any; difficulty: string }>;
  difficultyLevels: string[];
}

export const AdvancedAnalytics: React.FC<AdvancedAnalyticsProps> = ({
  categories,
  assessments,
  difficultyLevels,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate()
  // Redux selectors
  const {
    attemptsData,
    attemptQuestionData,
    attemptQuestionLoading,
  } = useSelector((state: RootState) => state.analytics);

  // Filter states
  const [filters, setFilters] = useState<AssessmentAttemptsQuery>({
    page: 1,
    limit: 20,
    sort: '-createdAt',
  });

  // UI states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    dispatch(fetchAnalyticsDashboard({ period: 'month' as any }));
    dispatch(fetchAssessmentAttempts(filters));
  }, [dispatch]);

  const handleFilterChange = (key: keyof AssessmentAttemptsQuery, value: any) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    dispatch(fetchAssessmentAttempts(newFilters));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    const newFilters = { ...filters, page: newPage + 1 };
    setFilters(newFilters);
    dispatch(fetchAssessmentAttempts(newFilters));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = { ...filters, limit: parseInt(event.target.value, 10), page: 1 };
    setFilters(newFilters);
    dispatch(fetchAssessmentAttempts(newFilters));
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, attempt: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedAttempt(attempt);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAttempt(null);
  };


  const handleViewDetails = () => {
    if (selectedAttempt?._id) {
      dispatch(fetchAttemptQuestionAnalytics(selectedAttempt._id));
      setDetailsDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setExpandedQuestions(new Set());
  };

  const toggleQuestionExpansion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const getAnswerStatusIcon = (isCorrect: boolean, selectedAnswer: string | null) => {
    if (selectedAnswer === null) {
      return <HelpOutline color="warning" />;
    }
    return isCorrect ? <CheckCircle color="success" /> : <Cancel color="error" />;
  };

  const getAnswerStatusColor = (isCorrect: boolean, selectedAnswer: string | null) => {
    if (selectedAnswer === null) return 'warning';
    return isCorrect ? 'success' : 'error';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'abandoned':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        {/* Dashboard Overview */}
        {/* <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{xs: 12, sm: 6, md: 3}}>
            <Card
              elevation={0}
              sx={{
                position: 'relative',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRadius: '20px',
                backgroundColor: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  boxShadow: theme.palette.mode === 'light'
                    ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                    : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <People sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700, mb: 1 }}>
                  {dashboardData?.overview?.totalUsers || 0}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Total Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{xs: 12, sm: 6, md: 3}}>
            <Card
              elevation={0}
              sx={{
                position: 'relative',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRadius: '20px',
                backgroundColor: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  boxShadow: theme.palette.mode === 'light'
                    ? `0 12px 40px ${alpha(theme.palette.success.main, 0.15)}`
                    : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Assignment sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h3" color="success.main" sx={{ fontWeight: 700, mb: 1 }}>
                  {dashboardData?.overview?.totalAttempts || 0}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Total Attempts
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{xs: 12, sm: 6, md: 3}}>
            <Card
              elevation={0}
              sx={{
                position: 'relative',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRadius: '20px',
                backgroundColor: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: alpha(theme.palette.warning.main, 0.3),
                  boxShadow: theme.palette.mode === 'light'
                    ? `0 12px 40px ${alpha(theme.palette.warning.main, 0.15)}`
                    : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <TrendingUp sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700, mb: 1 }}>
                  {dashboardData?.overview?.averageScore?.toFixed(1) || 0}%
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Average Score
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{xs: 12, sm: 6, md: 3}}>
            <Card
              elevation={0}
              sx={{
                position: 'relative',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRadius: '20px',
                backgroundColor: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  boxShadow: theme.palette.mode === 'light'
                    ? `0 12px 40px ${alpha(theme.palette.info.main, 0.15)}`
                    : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Speed sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                <Typography variant="h3" color="info.main" sx={{ fontWeight: 700, mb: 1 }}>
                  {dashboardData?.overview?.passRate?.toFixed(1) || 0}%
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Pass Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid> */}

        {/* Filters */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            position: 'relative',
            height: '100%',
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            borderRadius: '20px',
            backgroundColor: theme.palette.mode === 'light' 
              ? 'rgba(255, 255, 255, 0.95)' 
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden',
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.3),
              boxShadow: theme.palette.mode === 'light'
                ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
            },
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
              <FilterList sx={{ mr: 1 }} />
              Advanced Filters
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={{xs: 12, sm: 6, md: 4}}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search users or assessments..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    }
                  }}
                />
              </Grid>

              <Grid size={{xs: 12, sm: 6, md: 2}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assessment</InputLabel>
                  <Select
                    value={filters.assessmentId || ''}
                    label="Assessment"
                    onChange={(e) => handleFilterChange('assessmentId', e.target.value)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="">All Assessments</MenuItem>
                    {assessments.map((assessment) => (
                      <MenuItem key={assessment._id} value={assessment._id}>
                        {assessment.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12, sm: 6, md: 2}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.categoryId || ''}
                    label="Category"
                    onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12, sm: 6, md: 2}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="abandoned">Abandoned</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12, sm: 6, md: 2}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={filters.difficulty || ''}
                    label="Difficulty"
                    onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {difficultyLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* <Grid size={{xs: 12, sm: 6, md: 1}}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleExport('xlsx')}
                    startIcon={<Download />}
                    size="small"
                    sx={{ borderRadius: '12px', textTransform: 'none' }}
                  >
                    Export
                  </Button>
                </Stack>
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        {/* Assessment Attempts Table */}
        <Card
          elevation={0}
          sx={{
            position: 'relative',
            height: '100%',
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            borderRadius: '20px',
            backgroundColor: theme.palette.mode === 'light' 
              ? 'rgba(255, 255, 255, 0.95)' 
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden',
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.3),
              boxShadow: theme.palette.mode === 'light'
                ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
            },
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Assessment Attempts ({attemptsData?.summary?.totalAttempts || 0})
              </Typography>
              
              {attemptsData?.summary && (
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <Chip
                    label={`Completed: ${attemptsData.summary.completedAttempts}`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    label={`Avg Score: ${attemptsData.summary.averageScore.toFixed(1)}%`}
                    color="info"
                    variant="outlined"
                  />
                  <Chip
                    label={`Pass Rate: ${attemptsData.summary.passRate.toFixed(1)}%`}
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              )}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Assessment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attemptsData?.attempts?.map((attempt) => (
                    <TableRow key={attempt._id} hover>
                      <TableCell>
                        <Box onClick={()=>navigate('/profile/'+attempt.user?._id)} sx={{cursor:'pointer'}}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {attempt.user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {attempt.user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {attempt.assessment.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {attempt.assessment.category.name} • {attempt.assessment.difficulty}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={attempt.status}
                          color={getStatusColor(attempt.status) as any}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        {attempt.score ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {attempt.score.percentage}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {attempt.score.correctAnswers}/{attempt.score.totalQuestions}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDuration(attempt.timing.totalTimeSpent)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(attempt.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(attempt.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, attempt)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )) || []}
                </TableBody>
              </Table>
            </TableContainer>

            {attemptsData?.pagination && (
              <TablePagination
                component="div"
                count={attemptsData.pagination.totalItems}
                page={attemptsData.pagination.currentPage - 1}
                onPageChange={handlePageChange}
                rowsPerPage={filters.limit || 20}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
                showFirstButton
                showLastButton
              />
            )}
          </CardContent>
        </Card>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItemComponent onClick={handleViewDetails}>
            <Visibility sx={{ mr: 1 }} />
            View Details
          </MenuItemComponent>
          <MenuItemComponent onClick={handleViewDetails}>
            <AnalyticsIcon sx={{ mr: 1 }} />
            Question Analytics
          </MenuItemComponent>
        </Menu>

        {/* Question Details Dialog */}
        <Dialog
          open={detailsDialogOpen}
          onClose={handleCloseDetailsDialog}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
            sx: {
              borderRadius: '16px',
              minHeight: '80vh',
            }
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            pb: 2
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Question Analytics Details
              </Typography>
              {attemptQuestionData && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    {attemptQuestionData.attempt.user.name}
                  </Typography>
                  <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
                    {attemptQuestionData.attempt.assessment.title}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                    <Chip
                      label={`Score: ${attemptQuestionData.attempt.score?.percentage || 0}%`}
                      color={attemptQuestionData.attempt.score?.isPassed ? "success" : "error"}
                      variant="filled"
                      size="small"
                    />
                    <Chip
                      label={`${attemptQuestionData.attempt.score?.correctAnswers || 0}/${attemptQuestionData.attempt.score?.totalQuestions || 0} Correct`}
                      color="info"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`Attempt #${attemptQuestionData.attempt.attemptNumber}`}
                      color="default"
                      variant="outlined"
                      size="small"
                    />
                    {attemptQuestionData.attempt.isRetake && (
                      <Chip
                        label="Retake"
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {attemptQuestionData.attempt.user.email} • {attemptQuestionData.attempt.assessment.category.name} • {attemptQuestionData.attempt.assessment.difficulty}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Completed: {new Date(attemptQuestionData.attempt.timing.completedAt || attemptQuestionData.attempt.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton onClick={handleCloseDetailsDialog}>
              <Close />
            </IconButton>
          </DialogTitle>
          
          <Divider />
          
          <DialogContent sx={{ p: 3 }}>
            {attemptQuestionLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Typography>Loading question details...</Typography>
              </Box>
            ) : attemptQuestionData ? (
              <Box>
                {/* Summary Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="primary">
                        {attemptQuestionData.summary.totalQuestions}
                      </Typography>
                      <Typography variant="caption">Total Questions</Typography>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="success.main">
                        {attemptQuestionData.summary.correctAnswers}
                      </Typography>
                      <Typography variant="caption">Correct Answers</Typography>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="info.main">
                        {attemptQuestionData.summary.answeredQuestions}
                      </Typography>
                      <Typography variant="caption">Answered</Typography>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="warning.main">
                        {attemptQuestionData.summary.averageTimePerQuestion 
                          ? formatDuration(attemptQuestionData.summary.averageTimePerQuestion)
                          : '0s'
                        }
                      </Typography>
                      <Typography variant="caption">Avg Time</Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Questions List */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Questions ({attemptQuestionData.questions.length})
                </Typography>
                
                <Stack spacing={2}>
                  {attemptQuestionData.questions.map((question, index) => {
                    const isExpanded = expandedQuestions.has(question.questionId);
                    
                    return (
                      <Card key={question.questionId} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ pb: 2 }}>
                          {/* Question Header */}
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={2} 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleQuestionExpansion(question.questionId)}
                          >
                            <Box
                              sx={{
                                minWidth: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: getAnswerStatusColor(question.isCorrect, question.selectedAnswer) + '.main',
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            >
                              {index + 1}
                            </Box>
                            <Box flex={1}>
                              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                {question.questionText}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip
                                  size="small"
                                  icon={getAnswerStatusIcon(question.isCorrect, question.selectedAnswer)}
                                  label={
                                    question.selectedAnswer === null 
                                      ? 'Not Answered' 
                                      : question.isCorrect 
                                        ? 'Correct' 
                                        : 'Incorrect'
                                  }
                                  color={getAnswerStatusColor(question.isCorrect, question.selectedAnswer) as any}
                                />
                                <Chip
                                  size="small"
                                  label={`Your Answer: ${question.selectedAnswer || 'None'}`}
                                  variant="outlined"
                                  color={question.selectedAnswer ? (question.isCorrect ? 'success' : 'error') : 'default'}
                                />
                                <Chip
                                  size="small"
                                  label={`Correct: ${question.correctAnswer}`}
                                  variant="outlined"
                                  color="success"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Time: {formatDuration(question.timeSpent)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {question.difficulty}
                                </Typography>
                              </Stack>
                            </Box>
                            <IconButton size="small">
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>

                          {/* Expanded Content */}
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 3 }}>
                              {/* Answer Options */}
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                {Object.entries(question.options).map(([optionKey, optionValue]) => {
                                  const isCorrect = optionKey === question.correctAnswer;
                                  const isSelected = optionKey === question.selectedAnswer;
                                  const isWrongSelection = isSelected && !isCorrect;
                                  
                                  return (
                                    <Grid size={{ xs: 12, sm: 6 }} key={optionKey}>
                                      <Box
                                        sx={{
                                          p: 2,
                                          borderRadius: 2,
                                          border: '2px solid',
                                          borderColor: isCorrect 
                                            ? 'success.main' 
                                            : isWrongSelection 
                                              ? 'error.main' 
                                              : 'divider',
                                          bgcolor: (theme) => 
                                            isCorrect 
                                              ? alpha(theme.palette.success.main, 0.1)
                                              : isWrongSelection 
                                                ? alpha(theme.palette.error.main, 0.1)
                                                : 'transparent',
                                        }}
                                      >
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              fontWeight: 'bold',
                                              color: isCorrect 
                                                ? 'success.main' 
                                                : isWrongSelection 
                                                  ? 'error.main' 
                                                  : 'text.secondary'
                                            }}
                                          >
                                            {optionKey}.
                                          </Typography>
                                          <Typography variant="body2">
                                            {optionValue}
                                          </Typography>
                                        </Box>
                                        {isCorrect && (
                                          <Chip size="small" label="Correct" color="success" sx={{ mt: 1 }} />
                                        )}
                                        {isWrongSelection && (
                                          <Chip size="small" label="Your Answer" color="error" sx={{ mt: 1 }} />
                                        )}
                                      </Box>
                                    </Grid>
                                  );
                                })}
                              </Grid>

                              {/* Explanation */}
                              {question.explanation && (
                                <Box 
                                  sx={{ 
                                    mt: 2, 
                                    p: 2, 
                                    bgcolor: 'background.paper',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                                    Explanation
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    {question.explanation}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            ) : (
              <Typography>No question data available</Typography>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseDetailsDialog} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};